// apps/web/src/components/theme/ThemeProvider.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type Theme = 'light' | 'dark';

type ThemeContextValue = {
  theme: Theme;
  setTheme: (next: Theme) => void;
  toggle: () => void;
};

const STORAGE_KEY = 'gilding_theme';
const ThemeContext = createContext<ThemeContextValue | null>(null);

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage may be unavailable (Safari private, embedded webviews).
  }
  // The void-and-gold dark theme is the brand's face; light parchment is the
  // opt-in alternative via the toggle.
  return 'dark';
}

function applyThemeToDocument(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.remove('theme-light', 'theme-dark');
  root.classList.add(`theme-${theme}`);
  // For native form controls, scrollbars, etc.
  root.style.colorScheme = theme;
}

type ThemeProviderProps = {
  children: ReactNode;
  /** When true, also follow system changes if no user preference is stored. */
  followSystem?: boolean;
};

export function ThemeProvider({ children, followSystem = false }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => readInitialTheme());
  const firstApply = useRef(true);

  // Apply on mount + whenever theme changes. After the first paint, theme
  // swaps ride the View Transitions cross-fade where the browser supports it,
  // so light/dark melts over instead of snapping.
  useEffect(() => {
    if (firstApply.current) {
      firstApply.current = false;
      applyThemeToDocument(theme);
      return;
    }
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const doc = document as Document & {
      startViewTransition?: (cb: () => void) => unknown;
    };
    if (!reduceMotion && typeof doc.startViewTransition === 'function') {
      doc.startViewTransition(() => applyThemeToDocument(theme));
    } else {
      applyThemeToDocument(theme);
    }
  }, [theme]);

  // Persist on change.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* swallow */
    }
  }, [theme]);

  // Follow OS preference changes IF user hasn't manually overridden.
  useEffect(() => {
    if (!followSystem || typeof window === 'undefined') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (event: MediaQueryListEvent) => {
      const stored = (() => {
        try {
          return window.localStorage.getItem(STORAGE_KEY);
        } catch {
          return null;
        }
      })();
      if (stored === 'light' || stored === 'dark') return; // explicit user choice wins
      setThemeState(event.matches ? 'dark' : 'light');
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [followSystem]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const toggle = useCallback(() => {
    setThemeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggle }),
    [theme, setTheme, toggle]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return ctx;
}

/* ------------------------------------------------------------------
   ThemeToggle — small icon button.
   Sun / Moon are bespoke SVGs (hairline strokes, slight serif energy)
   so the toggle doesn't drag in lucide-style generic glyphs.
   ------------------------------------------------------------------ */

type ThemeToggleProps = {
  className?: string;
};

export function ThemeToggle({ className }: ThemeToggleProps) {
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      aria-pressed={isDark}
      title={isDark ? 'Light' : 'Dark'}
      className={
        'group inline-flex h-9 w-9 items-center justify-center rounded-sm ' +
        'text-ink-soft transition-colors duration-200 hover:text-ink ' +
        (className ?? '')
      }
    >
      <span className="relative block h-5 w-5">
        {/* Sun — visible in light theme. Hairline rays, soft inner circle. */}
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          className={
            'absolute inset-0 h-5 w-5 transition-[opacity,transform] duration-300 ' +
            (isDark ? 'opacity-0 -rotate-45 scale-75' : 'opacity-100 rotate-0 scale-100')
          }
        >
          <circle cx="10" cy="10" r="3.4" fill="currentColor" />
          <g stroke="currentColor" strokeWidth="1.1" strokeLinecap="round">
            <line x1="10" y1="2.2" x2="10" y2="4" />
            <line x1="10" y1="16" x2="10" y2="17.8" />
            <line x1="2.2" y1="10" x2="4" y2="10" />
            <line x1="16" y1="10" x2="17.8" y2="10" />
            <line x1="4.4" y1="4.4" x2="5.7" y2="5.7" />
            <line x1="14.3" y1="14.3" x2="15.6" y2="15.6" />
            <line x1="4.4" y1="15.6" x2="5.7" y2="14.3" />
            <line x1="14.3" y1="5.7" x2="15.6" y2="4.4" />
          </g>
        </svg>
        {/* Moon — visible in dark theme. Crescent + one tiny "star" speck. */}
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          className={
            'absolute inset-0 h-5 w-5 transition-[opacity,transform] duration-300 ' +
            (isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 rotate-45 scale-75')
          }
        >
          <path
            d="M14.6 12.2a6 6 0 0 1-7.4-7.6 6.2 6.2 0 1 0 7.4 7.6Z"
            fill="currentColor"
          />
          <circle cx="15.3" cy="5" r="0.7" fill="currentColor" opacity="0.85" />
        </svg>
      </span>
    </button>
  );
}
